import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';

import { API_URL, useCreditBalance } from '@app/api';
import {
  Button,
  CenteredLoader,
  Country,
  useUnlockFeature,
  PaymentMethodsList,
  DotsMenu,
  MenuItem,
  AddPaymentMethodButton,
  BillingMethodForm,
  BillingInformationForm,
  BillingInfoText,
} from 'components';
import {
  BillingMethod,
  SubscriptionStatus,
  useBillingInformationsByOrga,
  useCurrentOrganizationId,
  useCurrentSubscriptions,
  useInvoices,
  useRemovePaymentMethod,
  useSetDefaultPaymentMethod,
  useUpdateBillingMethod,
} from 'queries';
import {
  AnimatedSlidingDiv,
  CenteredModal,
  InformationBox,
  useBoolModalState,
  useToast,
} from '@app/core';
import { SettingContent } from './components/SettingContent';
import { SubscriptionDetails } from './components/SubscriptionDetails';
import { InvoiceLine } from './components/InvoiceLine';

const useStyles = createUseStyles((theme: Theme) => ({
  buttonLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: theme.marginBase * 2,
    gap: theme.marginBase,
  },
  title: {
    ...theme.fonts.h2,
    flex: 1,
  },
  label: {
    ...theme.fonts.h5,
    marginBottom: theme.marginBase,
    marginTop: theme.marginBase * 2,
  },
  box: {
    marginBottom: theme.marginBase * 2,
  },
  column: {
    flex: 1,
  },
  card: {
    ...theme.fonts.body,
    fontStyle: 'normal',
    color: theme.colors.inkPrimary,

    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase * 2,
  },
  billingMethod: {},
  billingMethodIcon: {},
  historyLink: {
    display: 'flex',
    alignItems: 'center',
    ...theme.fonts.body,
    color: theme.colors.blueAccessible,
    '&:hover, &:active, &:focus': {
      color: theme.hoverColors.blueAccessible,
    },
  },
  link: {
    textDecoration: 'underline',
  },
  linkIcon: {
    fontSize: theme.iconSize.regular,
    marginLeft: theme.marginBase,
  },
  section: {
    marginTop: theme.marginBase * 4,
  },
  paymentMethodContainer: {},
  notUsablePaymentMethod: {
    opacity: 0.6,
  },
  seeNotUsable: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  invoiceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
}));

export const BillingSettings = () => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const toast = useToast();

  const organizationId = useCurrentOrganizationId();
  const [isModalOpened, openModal, closeModale] = useBoolModalState(false);
  const [
    isBillingMethodModalOpened,
    openBillingMethodModal,
    closeBillingMethodModal,
  ] = useBoolModalState(false);

  const openSubscriptionModal = useUnlockFeature(true);
  const { data: invoices } = useInvoices(organizationId);

  const { data: subscriptions } = useCurrentSubscriptions();
  const { mutateAsync: setDefaultPaymentMethod } =
    useSetDefaultPaymentMethod(organizationId);
  const { mutate: removePaymentMethod } =
    useRemovePaymentMethod(organizationId);

  const { data: billingInfo, isLoading } =
    useBillingInformationsByOrga(organizationId);
  const { data: creditBalance } = useCreditBalance(organizationId);

  const { mutateAsync: updateBillingMethod } = useUpdateBillingMethod(
    billingInfo?.id,
  );
  const billingMethod = billingInfo?.defaultStudyBillingMethod;
  const [notUsableOpened, showNotUsable, closeNotUsable] =
    useBoolModalState(false);

  const hasActiveSubscription = useMemo(
    () =>
      subscriptions?.some(
        (s) =>
          ![
            SubscriptionStatus.CANCELED,
            SubscriptionStatus.INCOMPLETE_EXPIRED,
            SubscriptionStatus.PAUSED,
            SubscriptionStatus.UNPAID,
          ].includes(s.status) && !s.cancelAt,
      ),
    [subscriptions],
  );

  const onBillingMethodChange = useCallback(
    async (billingMethod: BillingMethod) => {
      try {
        await updateBillingMethod({ billingMethod });
        toast.saved();
        closeBillingMethodModal();
      } catch (e) {
        toast.exception(e);
      }
    },
    [updateBillingMethod, toast, closeBillingMethodModal],
  );

  if (!billingInfo || isLoading) {
    return <CenteredLoader />;
  }

  return (
    <SettingContent>
      <h1 className={classes.title}>{t('billing.settings.title')}</h1>
      <section className={classes.section}>
        <h2 className={classes.label}>{t('billing.subscription.title')}</h2>

        {subscriptions?.length ? (
          subscriptions?.map((subscription) => (
            <SubscriptionDetails
              subscription={subscription}
              key={subscription.id}
            />
          ))
        ) : (
          <SubscriptionDetails />
        )}
        <div className={classes.buttonLine}>
          {!billingInfo?.stripeId || !subscriptions?.length ? (
            <Button
              text={t('billing.subscription.subscribe')}
              small
              onClick={openSubscriptionModal}
              color="accent"
            />
          ) : (
            <>
              <Button
                text={t('billing.subscription.manage')}
                small
                href={`${API_URL}/v2/organizations/${organizationId}/client-portal`}
                target="_blank"
                rel="noopener"
                variant="muted"
                color="accent"
              />
              {!hasActiveSubscription && (
                <Button
                  text={t('billing.subscription.upgrade')}
                  small
                  onClick={openSubscriptionModal}
                />
              )}
            </>
          )}
        </div>
      </section>
      <section className={classes.section}>
        <h2 className={classes.label}>
          {t('billing.billingMethod.defaultTitle')}
        </h2>
        {billingMethod ? (
          <InformationBox
            variant="question"
            icon={
              billingMethod === BillingMethod.SEND_INVOICE
                ? 'ri-file-list-3-line'
                : 'ri-bank-card-line'
            }
            title={t(`billing.billingMethod.labels.${billingMethod}`)}
            text={t(`billing.billingMethod.info.${billingMethod}`)}
          />
        ) : (
          <InformationBox
            variant="question"
            title={t('billing.billingMethod.labels.NONE')}
          />
        )}
        <div className={classes.buttonLine}>
          <Button
            text={t('billing.settings.edit')}
            small
            onClick={openBillingMethodModal}
            color="accent"
          />
        </div>
        <CenteredModal
          title={t('billing.billingMethod.defaultTitle')}
          isOpen={isBillingMethodModalOpened}
          onRequestClose={closeBillingMethodModal}
        >
          <BillingMethodForm
            requireInvoiceFeature
            onSuccess={onBillingMethodChange}
            billingMethod={billingMethod || BillingMethod.CREDIT_CARD}
            context="study"
            creditBalance={creditBalance}
            strictCreditBalanceCheck={false}
          />
        </CenteredModal>
      </section>
      <section className={classes.section}>
        <h2 className={classes.label}>{t('billing.informations.title')}</h2>
        <BillingInfoText billingInfo={billingInfo} />
        <div className={classes.buttonLine}>
          <Button
            text={t('billing.settings.edit')}
            small
            onClick={openModal}
            color="accent"
          />
        </div>
        <CenteredModal
          isOpen={isModalOpened}
          onRequestClose={closeModale}
          title={t('billing.informations.title')}
        >
          <BillingInformationForm
            billingInformation={billingInfo}
            onSuccess={closeModale}
          />
        </CenteredModal>
      </section>

      {!!billingInfo.stripeId && (
        <section className={classes.section}>
          <h2 className={classes.label}>{t('billing.paymentMethod.title')}</h2>
          <div className={classes.paymentMethodContainer}>
            {organizationId && (
              <>
                <AddPaymentMethodButton
                  organizationId={organizationId}
                  selectableStyle
                />
                <PaymentMethodsList
                  organizationId={organizationId}
                  filter="usable"
                >
                  {(paymentMethod) => (
                    <DotsMenu horizontal>
                      <MenuItem
                        label={t('billing.paymentMethod.actions.setDefault')}
                        onClick={() =>
                          setDefaultPaymentMethod({
                            paymentMethod: paymentMethod.id,
                          })
                        }
                      />
                      <MenuItem
                        label={t('billing.paymentMethod.actions.remove')}
                        onClick={() => removePaymentMethod(paymentMethod.id)}
                      />
                    </DotsMenu>
                  )}
                </PaymentMethodsList>
              </>
            )}
          </div>

          {organizationId && (
            <>
              <AnimatedSlidingDiv isOpen={notUsableOpened} maxHeight={2000}>
                <PaymentMethodsList
                  organizationId={organizationId}
                  filter="notUsable"
                  elementClassName={classes.notUsablePaymentMethod}
                >
                  {(paymentMethod) => (
                    <DotsMenu horizontal>
                      <MenuItem
                        label={t('billing.paymentMethod.actions.remove')}
                        onClick={() => removePaymentMethod(paymentMethod.id)}
                      />
                    </DotsMenu>
                  )}
                </PaymentMethodsList>
              </AnimatedSlidingDiv>

              <div className={classes.seeNotUsable}>
                <Button
                  variant="transparent"
                  color="accent"
                  onClick={notUsableOpened ? closeNotUsable : showNotUsable}
                  text={
                    notUsableOpened
                      ? t('billing.paymentMethod.actions.hidePrevious')
                      : t('billing.paymentMethod.actions.seePrevious')
                  }
                />
              </div>
            </>
          )}
        </section>
      )}
      <section className={classes.section}>
        <h2 className={classes.label}>{t('billing.paymentMethod.invoices')}</h2>
        <div className={classes.invoiceList}>
          {invoices?.map((invoice) => (
            <InvoiceLine key={invoice.id} invoice={invoice} />
          ))}
        </div>
      </section>

      {!!billingInfo.stripeId && (
        <section className={classes.section}>
          {/* eslint-disable react/jsx-no-target-blank */}
          <a
            href={`${API_URL}/v2/organizations/${organizationId}/client-portal`}
            className={classes.historyLink}
            target="_blank"
            rel="noopener"
          >
            <span className={classes.link}>
              {t('billing.paymentMethod.stripeLink')}
            </span>
            <i
              className={classnames(classes.linkIcon, 'ri-arrow-right-line')}
            />
          </a>
        </section>
      )}
    </SettingContent>
  );
};
